import { ref, computed, watch } from 'vue'
import { defineStore } from 'pinia'
import { ubiquAxios } from '@/main'
import axios from 'axios'
import { useRoute, useRouter } from 'vue-router'

export const SECURE_ELEMENT_TYPES = {
  RSE: 'rse',
  SSE: 'sse',
}

export type SecureElementType = keyof typeof SECURE_ELEMENT_TYPES
export type Identity = {
  sub: string
  secure_elements: SecureElementType[]
  roles: string[]
  name: string
  preferred_username: string
  organisation: { name: string; id: string }
}

type SessionActiveUser = Identity

export type ActiveUser = SessionActiveUser | null

export interface User {
  identity_subject: string
}
export const publicRoutes = [
  'authorize',
  'start',
  undefined,
]

export const useSessionStore = defineStore('sessionStore', () => {
  const activeUser = ref<ActiveUser>(null)
  const organisationMembers = ref<User[]>([])
  const remainingUsers = ref<User[]>([])
  const router = useRouter()
  const authenticating = ref(false)

  const updateActiveUser = (user: ActiveUser) => (activeUser.value = user)
  const updateOrganisationMembers = (users: User[]) => (organisationMembers.value = users)
  const updateRemainingUsers = (users: User[]) => (remainingUsers.value = users)

  const route = useRoute()

  watch(route, (curr) => {
    if (!publicRoutes.includes(curr.name as string | undefined)) {
      getIdentity()
    }
  })

  const resetStore = () => {
    activeUser.value = null
    organisationMembers.value = []
    remainingUsers.value = []
  }

  const getIdentity = async (force = false) => {
    if (publicRoutes.includes(route.name as string) && !force) return
    authenticating.value = true
    try {
      const res = (
        await ubiquAxios.get(
          `${import.meta.env.VITE_API_IDENTITY}user_info`,
        )
      ).data
      updateActiveUser(res)
    } finally {
      authenticating.value = false
    }
  }

  const init = async (force = false) => {
    await getIdentity(force)
  }

  const logout = async () => {
    const simpleAxios = axios.create({ withCredentials: true })
    await simpleAxios.post(`${import.meta.env.VITE_BFF_URL}/logout/`)
    resetStore()
    router.push({ name: 'start' })
  }

  const userName = computed(() => activeUser.value?.preferred_username || activeUser.value?.name)

  return {
    activeUser,
    userName,
    organisationMembers,
    remainingUsers,
    authenticating,
    updateActiveUser,
    updateOrganisationMembers,
    updateRemainingUsers,
    logout,
    init,
    resetStore,
  }
})
